import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useUser } from './UserContext';

const WebSocketContext = createContext();

/**
 * WebSocketProvider - Manages a single, persistent WebSocket connection for the entire app
 * 
 * This provider establishes and maintains a global WebSocket connection that can be shared
 * across all components. It handles connection state, reconnection logic, and message routing.
 */
export function WebSocketProvider({ children }) {
	const { isAuthenticated, isLoading: userLoading, token } = useUser();
	const [isConnected, setIsConnected] = useState(false);
	const [isConnecting, setIsConnecting] = useState(false);
	const [connectionError, setConnectionError] = useState(null);

	const wsRef = useRef(null);
	const reconnectTimeoutRef = useRef(null);
	const messageHandlersRef = useRef(new Map()); // topic -> Set of handlers
	const reconnectAttemptsRef = useRef(0);
	const isConnectingRef = useRef(false);
	const handleMessageRef = useRef(null);

	const maxReconnectAttempts = 10;
	const reconnectInterval = 3000;
	const websocketUrl = useMemo(() => {
		// Use environment variable if available
		if (process.env.REACT_APP_WS_URL) {
			return process.env.REACT_APP_WS_URL + '/api/ws';
		}
		// Custom domain - route WebSocket to the service subdomain
		if (window.location.hostname === 'getrepcheck.com' || window.location.hostname === 'www.getrepcheck.com') {
			return 'wss://service.getrepcheck.com/api/ws';
		}
		return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.hostname}:4000/api/ws`;
	}, []);

	/**
	 * Handle incoming WebSocket messages
	 */
	const handleMessage = useCallback((event) => {
		try {
			const message = JSON.parse(event.data);
			const { type, payload } = message;

			// Route messages based on type. This table MUST mirror what
			// backend/services/ScanProgressBroadcaster.js actually broadcasts -
			// it previously mapped only 3 of the 11 message types, so
			// scan.list.updated (live dashboard/list refresh), analysis progress
			// and scraper health were all silently dropped, and every consumer
			// compensated by polling the API on an interval instead.
			let topic = null;
			switch (type) {
				case 'scan.progress':
				case 'scan.status':
				case 'scan.state.update':
				case 'scan.queued':
				case 'analysis.progress':
				case 'window.analysis.progress':
					topic = `scan:${payload?.scanId}`;
					break;
				case 'scan.list.updated':
					// Backend publishes to user:{userId}:scans; the payload carries
					// the id so the router doesn't need auth state of its own.
					topic = payload?.userId ? `user:${payload.userId}:scans` : null;
					break;
				case 'admin.queue.updated':
					topic = 'admin:queue';
					break;
				case 'system.health':
					topic = 'admin:system';
					break;
				case 'scraper.offline':
				case 'scraper.online':
				case 'scraper.session.invalid':
				case 'scraper.session.warning':
					topic = 'scraper:health';
					break;
				case 'heartbeat.run.update':
					topic = payload?.runId ? `heartbeat:${payload.runId}` : null;
					break;
				default:
					topic = null;
			}

			if (topic) {
				const handlers = messageHandlersRef.current.get(topic);
				if (handlers) {
					handlers.forEach(handler => {
						try {
							handler(message);
						} catch (error) {
							console.error('Error in message handler:', error);
						}
					});
				}
			}

			// Handle connection-specific messages
			if (type === 'connection.established') {
			} else if (type === 'error') {
				console.error('WebSocket server error:', payload.message);
			}
		} catch (error) {
			console.error('Error parsing WebSocket message:', error);
		}
	}, []);

	// Update the ref when handleMessage changes
	useEffect(() => {
		handleMessageRef.current = handleMessage;
	}, [handleMessage]);

	/**
	 * Send a message through the WebSocket connection
	 */
	const send = useCallback((message) => {
		if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
			wsRef.current.send(JSON.stringify(message));
			return true;
		}
		console.warn('WebSocket not connected, unable to send message:', message);
		return false;
	}, []);

	/**
	 * Register a message handler for a specific topic
	 */
	const registerMessageHandler = useCallback((topic, handler) => {
		if (!messageHandlersRef.current.has(topic)) {
			messageHandlersRef.current.set(topic, new Set());
		}
		messageHandlersRef.current.get(topic).add(handler);

		return () => {
			const handlers = messageHandlersRef.current.get(topic);
			if (handlers) {
				handlers.delete(handler);
				if (handlers.size === 0) {
					messageHandlersRef.current.delete(topic);
				}
			}
		};
	}, []);

	/**
	 * Establish WebSocket connection
	 */
	const connect = useCallback(() => {
		if (isConnectingRef.current || (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING)) {
			return;
		}

		isConnectingRef.current = true;
		setIsConnecting(true);
		setConnectionError(null);

		try {
			// Prefer cookie auth (token null); else use query token for API clients
			const wsUrl = token
				? `${websocketUrl}?token=${encodeURIComponent(token)}`
				: websocketUrl;
			console.log(`Attempting to connect WebSocket to: ${websocketUrl}`);
			const ws = new WebSocket(wsUrl);
			wsRef.current = ws;

			ws.onopen = () => {
				setIsConnected(true);
				isConnectingRef.current = false;
				setIsConnecting(false);
				setConnectionError(null);
				reconnectAttemptsRef.current = 0;
			};

			ws.onmessage = handleMessageRef.current;

			ws.onclose = (event) => {
				setIsConnected(false);
				isConnectingRef.current = false;
				setIsConnecting(false);
				wsRef.current = null;

				// Attempt reconnection if not a normal closure
				if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
					const delay = Math.min(reconnectInterval * Math.pow(1.5, reconnectAttemptsRef.current), 30000);

					reconnectTimeoutRef.current = setTimeout(() => {
						reconnectAttemptsRef.current++;
						connect();
					}, delay);
				} else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
					setConnectionError('Unable to connect to server. Please refresh the page.');
				}
			};

			ws.onerror = (error) => {
				console.error('WebSocket error:', error);
				setConnectionError('Connection error occurred');
				isConnectingRef.current = false;
				setIsConnecting(false);
			};
		} catch (error) {
			console.error('Error creating WebSocket:', error);
			setConnectionError('Failed to create connection');
			isConnectingRef.current = false;
			setIsConnecting(false);
		}
	}, [token, websocketUrl]);

	/**
	 * Manually reconnect WebSocket
	 */
	const reconnect = useCallback(() => {
		if (wsRef.current) {
			wsRef.current.close();
		}
		if (reconnectTimeoutRef.current) {
			clearTimeout(reconnectTimeoutRef.current);
		}
		reconnectAttemptsRef.current = 0;
		connect();
	}, [connect]);

	/**
	 * Clean up connections and timeouts
	 */
	// Initialize connection on mount - only when user is authenticated
	useEffect(() => {
		// Don't connect if user context is still loading
		if (userLoading) {
			return;
		}

		// Only connect if user is authenticated
		if (isAuthenticated()) {
			// Inline the connection logic to avoid function dependencies
			const connectWebSocket = () => {
				try {
					if (wsRef.current?.readyState === WebSocket.OPEN) {
						return; // Already connected
					}

					if (isConnectingRef.current) {
						return; // Already connecting
					}

					isConnectingRef.current = true;
					setIsConnecting(true);
					setConnectionError(null);

					const wsUrl = token
						? `${websocketUrl}?token=${encodeURIComponent(token)}`
						: websocketUrl;
					const ws = new WebSocket(wsUrl);
					wsRef.current = ws;

					// CRITICAL: wire incoming messages. This inline connect path (the
					// one actually used on mount) previously set onopen/onclose/onerror
					// but NOT onmessage - so the socket connected yet every
					// scan.progress / scan.status / admin.queue message was silently
					// dropped. That broke live scan progress and real-time stat updates
					// across the app. Route through the ref so the latest handler is used.
					ws.onmessage = (event) => {
						if (handleMessageRef.current) handleMessageRef.current(event);
					};

					ws.onopen = () => {
						// WebSocket connected
						isConnectingRef.current = false;
						setIsConnecting(false);
						setIsConnected(true);
						reconnectAttemptsRef.current = 0;
					};

					ws.onclose = (event) => {
						console.log('WebSocket disconnected:', event.code, event.reason);
						isConnectingRef.current = false;
						setIsConnecting(false);
						setIsConnected(false);

						// Attempt to reconnect if not a clean close
						if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
							const delay = Math.min(reconnectInterval * Math.pow(1.5, reconnectAttemptsRef.current), 30000);

							reconnectTimeoutRef.current = setTimeout(() => {
								reconnectAttemptsRef.current++;
								connectWebSocket();
							}, delay);
						} else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
							setConnectionError('Unable to connect to server. Please refresh the page.');
						}
					};

					ws.onerror = (error) => {
						console.error('WebSocket error:', error);
						setConnectionError('Connection error occurred');
						isConnectingRef.current = false;
						setIsConnecting(false);
					};
				} catch (error) {
					console.error('Error creating WebSocket:', error);
					setConnectionError('Failed to create connection');
					isConnectingRef.current = false;
					setIsConnecting(false);
				}
			};

			connectWebSocket();

			// Snapshot for the cleanup: by the time it runs, `.current` may point
			// at a handlers map registered by a later effect run, and clearing THAT
			// would drop live subscriptions rather than this run's.
			const handlersAtSetup = messageHandlersRef.current;
			// Return cleanup function
			return () => {
				if (reconnectTimeoutRef.current) {
					clearTimeout(reconnectTimeoutRef.current);
				}
				if (wsRef.current) {
					wsRef.current.close(1000, 'Component unmounting');
				}
				handlersAtSetup.clear();
				isConnectingRef.current = false;
			};
		} else {
			// Clean up any existing connection if user is not authenticated
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current);
			}
			if (wsRef.current) {
				wsRef.current.close(1000, 'User not authenticated');
			}
			messageHandlersRef.current.clear();
			isConnectingRef.current = false;
			setIsConnecting(false);
			setIsConnected(false);
		}
	}, [userLoading, websocketUrl, token, isAuthenticated]); // Token required for authenticated WebSocket handshake

	// Provide context value
	const contextValue = useMemo(() => ({
		isConnected,
		isConnecting,
		connectionError,
		send,
		reconnect,
		registerMessageHandler,
	}), [isConnected, isConnecting, connectionError, send, reconnect, registerMessageHandler]);

	return (
		<WebSocketContext.Provider value={contextValue}>
			{children}
		</WebSocketContext.Provider>
	);
}

/**
 * Hook to access WebSocket context
 */
export function useWebSocket() {
	const context = useContext(WebSocketContext);
	if (!context) {
		throw new Error('useWebSocket must be used within a WebSocketProvider');
	}
	return context;
}

export default WebSocketContext;
